import {
  normalizeSupplierMobile,
  SUPPLIER_EMAIL_REGEX,
  SUPPLIER_MOBILE_REGEX,
} from '@/utils/supplierValidation';
import type { EntityImportConfig } from '../types';



export const customerImportConfig: EntityImportConfig = {
  entityId: 'customer',
  title: 'Import Customers',
  description: 'Download a template, map columns, then validate and import customers.',
  doctypeLabel: 'POS Customer',
  fields: [
    { fieldname: 'customer_name', label: 'Customer Name', required: true, fieldtype: 'Data' },
    {
      fieldname: 'mobile',
      label: 'Mobile',
      required: false,
      fieldtype: 'Data',
      hint: 'Optional; must be unique per company when provided',
    },
    { fieldname: 'email', label: 'Email', required: false, fieldtype: 'Data' },
    { fieldname: 'city', label: 'City', required: false, fieldtype: 'Data' },
    { fieldname: 'address', label: 'Address', required: false, fieldtype: 'Text' },
    { fieldname: 'cnic', label: 'CNIC', required: false, fieldtype: 'Data' },
    { fieldname: 'ntn', label: 'NTN', required: false, fieldtype: 'Data' },
    { fieldname: 'customer_type', label: 'Customer Type', required: false, fieldtype: 'Data' },
    {
      fieldname: 'opening_balance',
      label: 'Opening Balance',
      required: false,
      fieldtype: 'Data',
      hint: 'Optional. + = customer owes you (outstanding). − = customer advance',
    },
  ],
  headerAliases: {
    customer_name: ['customer name', 'name', 'client', 'client name', 'customer'],
    mobile: ['mobile', 'phone', 'phone number', 'contact', 'contact number', 'cellphone', 'cell'],
    email: ['email', 'email address', 'e-mail'],
    city: ['city', 'city name'],
    address: ['address', 'addr', 'location'],
    cnic: ['cnic', 'cnic number', 'national id', 'cnic (fbr)'],
    ntn: ['ntn', 'ntn number', 'tax registration', 'ntn (fbr)'],
    customer_type: ['customer type', 'type', 'client type'],
    opening_balance: ['opening balance', 'opening amount', 'receivable', 'outstanding'],
  },
  localStorageKey: 'data-import:customer',
  onSuccessInvalidateKeys: [['customers']],
  skipReasonLabels: {
    invalid_mobile: 'Invalid mobile number',
    duplicate_mobile: 'Duplicate mobile in file',
    invalid_email: 'Invalid email',
    already_exists: 'Customer already exists for this company',
    manual_skip: 'Manually skipped',
    invalid_required: 'Required field missing',
  },
  normalizeRow: (raw) => {
    const mobileRaw = (raw.mobile || '').trim();
    const mobile = mobileRaw ? normalizeSupplierMobile(mobileRaw) : '';
    return {
      ...raw,
      customer_name: (raw.customer_name || '').trim(),
      mobile,
      email: (raw.email || '').trim(),
      city: (raw.city || '').trim(),
      address: (raw.address || '').trim(),
      cnic: (raw.cnic || '').trim(),
      ntn: (raw.ntn || '').trim(),
      customer_type: (raw.customer_type || 'Retailer').trim(),
      opening_balance: (raw.opening_balance || '').trim(),
    };
  },
  clientValidateRow: (row, ctx) => {
    const name = row.customer_name?.trim() || '';
    if (!name) {
      return { reason: 'invalid_required', label: 'Customer name is required' };
    }

    const mobile = row.mobile || '';
    if (mobile) {
      if (!SUPPLIER_MOBILE_REGEX.test(mobile)) {
        return {
          reason: 'invalid_mobile',
          label: 'Enter a valid Pakistani mobile (e.g. 03001234567)',
        };
      }
      const prev = ctx.seenMobiles.get(mobile);
      if (prev !== undefined) {
        return {
          reason: 'duplicate_mobile',
          label: `Duplicate mobile (also on row ${prev})`,
        };
      }
      ctx.seenMobiles.set(mobile, ctx.rowNumber);
    }

    if (row.email && !SUPPLIER_EMAIL_REGEX.test(row.email)) {
      return { reason: 'invalid_email', label: 'Enter a valid email address' };
    }

    return null;
  },
  api: {
    downloadTemplate: '/method/saas_pos.api.data_import.api.download_template',
    importRows: '/method/saas_pos.api.data_import.api.import_rows',
  },
};
